import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { endOfWeek, format, parseISO, startOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import {
  Locale,
  useAuthUserRestaurant,
  useGetRestaurantWeekMenusQuery,
} from '@durma-soft/gros-sdk';

import { Modal } from '@/components/shared/modal';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Button } from '@/components/shared/shadcn-ui/button';
import { WeekPicker } from '@/components/shared/week-picker';
import { WeekMenuPrint } from '@/components/restaurant/menus/week-menu-print';
import {
  buildWeekMenuPrintData,
  formatWeekRangeLabel,
} from '@/components/restaurant/menus/week-menu-print-data';

import print from '@/lib/print/print';
import { locales } from '@/config/global/languages';

const weekStartOf = (date: string) =>
  startOfWeek(parseISO(date), { weekStartsOn: 1 });

interface PrintWeekMenuButtonProps {
  date: string;
}

export const PrintWeekMenuButton = ({ date }: PrintWeekMenuButtonProps) => {
  const { t, i18n } = useTranslation();
  const locale = locales[i18n.language as Locale];
  const restaurant = useAuthUserRestaurant();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [weekStart, setWeekStart] = useState<Date>(() => weekStartOf(date));

  const weekMenus = useGetRestaurantWeekMenusQuery({
    restaurant_id: restaurant.id,
    week_start: format(weekStart, 'yyyy-MM-dd'),
  });

  const printData = useMemo(
    () =>
      weekMenus.data
        ? buildWeekMenuPrintData(weekMenus.data, {
            locale,
            shiftWord: t('menu.shift'),
          })
        : undefined,
    [weekMenus.data, locale, t],
  );
  const dayCount = printData?.days.length ?? 0;

  const openModal = () => {
    setWeekStart(weekStartOf(date));
    setIsModalOpen(true);
  };

  const handlePrint = () => {
    if (!printData || dayCount === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error(t('menu.print.popup-blocked'));
      return;
    }

    const weekLabel = formatWeekRangeLabel(
      weekStart,
      endOfWeek(weekStart, { weekStartsOn: 1 }),
    );

    try {
      print({
        printWindow,
        title: `${restaurant.name} – ${weekLabel}`,
        pageSize: 'A4 landscape',
        pageMargin: '1cm',
        content: (
          <WeekMenuPrint
            restaurantName={restaurant.name}
            weekLabel={weekLabel}
            data={printData}
          />
        ),
      });
      setIsModalOpen(false);
    } catch (error) {
      printWindow.close();
      toast.error(t('menu.print.error-msg'));
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openModal}
        className="flex gap-1.5 print:hidden"
      >
        <span>{t('menu.print.title')}</span>
        <Printer className="h-4 w-4" />
      </Button>

      <Modal
        isModalOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title={t('menu.print.title')}
        description={t('menu.print.description')}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="print-week-picker">{t('menu.print.week')}</Label>
            <WeekPicker
              id="print-week-picker"
              weekStart={weekStart}
              setWeekStart={setWeekStart}
            />
          </div>

          <p className="text-sm text-muted-foreground">
            {weekMenus.isLoading
              ? t('menu.print.loading')
              : weekMenus.isError
                ? t('menu.error-loading')
                : dayCount === 0
                  ? t('menu.print.no-menus')
                  : t('menu.print.days-with-menus', { count: dayCount })}
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handlePrint}
              disabled={weekMenus.isLoading || dayCount === 0}
              className="flex gap-1.5"
            >
              <span>{t('common.print')}</span>
              <Printer className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
